import type { BotTier } from '@runkr/config'
import { bodiesOverlap } from '../geom'
import type { CrateState, PlayerState, Projectile, World } from '../world'
import { rollout } from './rollout'
import type { Action, Outcome, Plan, Trouble } from './rollout'
import { airTicks, buildScene, minSpeed } from './scene'
import type { Scene } from './scene'

/** Terrain, hazard or trap trouble ahead that the bot must react to. */
export interface Obstacle {
  readonly kind: Trouble
  /** Stable identity: the reaction delay is drawn once per id. */
  readonly id: number
  /** Where it is cleared (left edge past it); the goal of a jump. */
  readonly clearX: number
  /** A bounce pad comes first: keep running, the pad carries the bot over. */
  readonly padFirst: boolean
}

/** What one bot sees this tick. */
export interface View {
  readonly scene: Scene
  /** Keep running (no buttons) for the look-ahead horizon. */
  readonly run: Outcome
  readonly obstacle: Obstacle | null
  /** A homing drone chasing the bot within the look-ahead (handled beside the obstacle). */
  readonly drone: Projectile | null
  /** Walking off a drop this deep is trouble (`dropAvoidPx`): bots stay on upper routes. */
  readonly maxDropPx: number
}

const TROUBLE_CODES: { readonly [K in Obstacle['kind']]: number } = {
  fall: 1,
  wall: 2,
  hazard: 4,
  trap: 5,
}
const ID_STRIDE = 1_000_000

function obstacleId(kind: Obstacle['kind'], key: number): number {
  return TROUBLE_CODES[kind] * ID_STRIDE + key
}

/** Current running speed (px/tick) used for horizons and the drone forecast. */
function speedOf(world: World, p: PlayerState): number {
  const boost = p.boostUntil > world.tick ? p.boostPct : 0
  return (world.cfg.baseSpeedPxPerTick * (100 + boost)) / 100
}

/** Longest rollout a decision may need: two look-aheads at the slowest speed plus two jumps. */
export function decisionHorizon(world: World, tier: BotTier): number {
  return Math.ceil((2 * tier.lookaheadPx) / minSpeed(world.cfg)) + 2 * airTicks(world.cfg)
}

/** A plan with the bot's current hold state. */
export function plan(
  view: View,
  mem: { holdJump: boolean },
  action: Action,
  delay: number,
  goalX: number,
): Plan {
  return {
    action,
    delay,
    keepHold: mem.holdJump,
    goalX,
    horizon: view.scene.horizon,
    touch: null,
    maxDropPx: view.maxDropPx,
  }
}

/**
 * Look ahead `lookaheadPx` by forecasting plain running (`rollout`): the first gap edge (or a
 * walk-off of `dropAvoidPx` or more, so bots keep to upper routes) or wall,
 * hazard active at the predicted arrival tick, or trap/spike ball in the way becomes the
 * obstacle. A bounce pad reached before it marks the obstacle `padFirst`. A homing drone chasing
 * the bot within `lookaheadPx` (either side) is reported separately.
 */
export function perceive(world: World, i: number, tier: BotTier, holdJump: boolean): View {
  const p = world.players[i]!
  const scene = buildScene(world, i, decisionHorizon(world, tier))
  // Long enough to reach the end of the look-ahead and see a walk-off there turn into a fall.
  const runHorizon =
    Math.ceil((tier.lookaheadPx + p.w) / speedOf(world, p)) + world.cfg.coyoteTicks + 1
  const run = rollout(world, i, scene, {
    action: 'run',
    delay: 0,
    keepHold: holdJump,
    goalX: Infinity,
    horizon: runHorizon,
    touch: null,
    maxDropPx: tier.dropAvoidPx,
  })
  let obstacle: Obstacle | null = null
  if (run.trouble && run.fromX - (p.x + p.w) <= tier.lookaheadPx) {
    obstacle = {
      kind: run.trouble,
      id: obstacleId(run.trouble, run.key),
      clearX: run.clearX,
      padFirst: run.bounced,
    }
  }
  const drone = incomingDrone(world, i, tier.lookaheadPx)
  return { scene, run, obstacle, drone, maxDropPx: tier.dropAvoidPx }
}

/** The nearest homing drone chasing player `i` whose centre is within `rangePx` of the bot's. */
export function incomingDrone(world: World, i: number, rangePx: number): Projectile | null {
  const p = world.players[i]!
  let best: Projectile | null = null
  let bestDx = rangePx
  for (const pr of world.projectiles) {
    if (pr.kind !== 'homing_drone' || pr.target !== i) continue
    const dx = Math.abs(pr.x + pr.w / 2 - (p.x + p.w / 2))
    if (dx <= bestDx) {
      best = pr
      bestDx = dx
    }
  }
  return best
}

/**
 * A full-height jump started `delay` ticks from now carries the bot over a locked drone: the
 * drone flies at constant velocity once locked, so step both forward (the bot's jump on a flat
 * floor) until the drone has fully passed, and fail on any overlap. `false` while unlocked
 * (it still re-aims) or not closing in.
 */
export function jumpDodges(world: World, i: number, pr: Projectile, delay: number): boolean {
  const p = world.players[i]!
  const { cfg } = world
  if (!pr.locked) return false
  const speed = speedOf(world, p)
  const closing = pr.vx > 0 ? pr.vx - speed : speed - pr.vx
  if (closing <= 0) return false
  const body = { x: p.x, y: p.y, w: p.w, h: p.h }
  const drone = { x: pr.x, y: pr.y, w: pr.w, h: pr.h }
  let vy = 0
  let rise = 0
  for (let k = 0; k <= delay + airTicks(cfg); k++) {
    if (k === delay) vy = -cfg.jumpVelocityPxPerTick
    const vy0 = vy
    if (k >= delay) vy += cfg.gravityPxPerTick2
    rise = Math.min(0, rise + (vy0 + vy) / 2)
    body.x += speed
    body.y = p.y + rise
    drone.x += pr.vx
    if (bodiesOverlap(body, drone)) return false
    const passed = pr.vx > 0 ? drone.x >= body.x + body.w : drone.x + drone.w <= body.x
    if (passed) return true
  }
  return false
}

/** Nearest available crate whose box lies ahead within `rangePx` of the bot's front edge. */
export function crateAhead(world: World, i: number, rangePx: number): CrateState | null {
  const p = world.players[i]!
  const size = world.cfg.items.crateSizePx
  let best: CrateState | null = null
  for (const c of world.crates) {
    if (world.tick < c.respawnAtTick || c.x + size <= p.x || c.x - (p.x + p.w) > rangePx) continue
    if (!best || c.x < best.x) best = c
  }
  return best
}

/** Some racing player has more progress than `i`. */
export function playerAhead(world: World, i: number): boolean {
  const own = world.players[i]!.progress
  return world.players.some((q, k) => k !== i && q.placement === 0 && q.progress > own)
}
